import { FC, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ChevronComboOutlinedIcon } from '@getgo/chameleon-icons/react'
import { PopoverComponent } from '@getgo/chameleon-web'
import { Button, Popover, Skeleton, Typography } from '@getgo/chameleon-web-react-wrapper'
import { Box } from '@material-ui/core'
import { ConnectedUserAccount } from '@ucc/admin-global-state'
import mixpanel from 'mixpanel-browser'

import { useAccountContext } from 'components/contexts/AccountContext'
import { useConnectedUserContext } from 'components/contexts/ConnectedUserContext'
import { useManagedAccounts } from 'hooks'
import { mixpanelEventNames } from 'utils'
import AccountMenu from './AccountMenu'
import AccountMenuForPartners from './AccountMenuForPartners'

const AccountSwitcher: FC = () => {
  const { currentAccount, accounts, isAccountLoaded } = useAccountContext()
  const { managedAccounts, managedAccountsByPartners, loading: managedAccountsLoading } = useManagedAccounts()
  const iAm = useConnectedUserContext()
  const { accountKey: pathAccountKey } = useParams() as { accountKey: string }

  const [iAmAccounts, setiAmAccounts] = useState<ConnectedUserAccount[] | null>(null)

  const popoverRef = useRef<any>(null)

  useEffect(() => {
    if (!iAm) return

    setiAmAccounts(
      (iAm.accounts ?? [])
        .filter((account) => account.adminRoles?.length)
        .map((account) => ({
          ...account,
          name: account.name || account.key,
          orgId: accounts?.find((acc) => acc.key === account.key)?.orgId,
        }))
        .sort((account1, account2) => account1.name.localeCompare(account2.name)),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iAm?.accounts, accounts?.length])

  // accounts not loaded yet
  if (!currentAccount || !iAm || managedAccountsLoading || !iAmAccounts) {
    return <Skeleton data-testid="account-switcher-skeleton" width={180} size="large" variant="rectangle" />
  }

  // only show account name when single account or direct
  // access (system user or partner user)
  if (iAmAccounts.length <= 1 && !managedAccounts.length) {
    return <>{currentAccount!.name}</>
  }

  const showComplexSelector = managedAccountsByPartners.length >= 1

  const closePopover = () => {
    const popover = popoverRef.current as PopoverComponent
    popover?.close && popover.close()
  }

  return (
    <>
      <Button
        id="accountSwitcher"
        data-testid="account-switcher"
        disabled={currentAccount!.key !== pathAccountKey || !isAccountLoaded}
        variant="tertiary"
        title={currentAccount!.name}
        onClick={() => {
          mixpanel.track(mixpanelEventNames.ACCOUNT_SWITCHER, {
            Action: `Toggled account switcher`,
          })
        }}
        trailingIcon={<ChevronComboOutlinedIcon />}
      >
        <Box
          // TODO: revisit
          maxWidth={284}
        >
          <Typography noWrap>{currentAccount!.name}</Typography>
        </Box>
      </Button>

      <Popover
        triggerId="accountSwitcher"
        menu
        position="bottom-start"
        ref={popoverRef}
        zIndex={1201}
        width={showComplexSelector ? 585 : 320}
      >
        {showComplexSelector ? (
          <AccountMenuForPartners
            accounts={iAmAccounts}
            closePopover={closePopover}
            managedAccountsByPartners={managedAccountsByPartners}
          />
        ) : (
          <AccountMenu accounts={iAmAccounts} closePopover={closePopover} />
        )}
      </Popover>
    </>
  )
}

export default AccountSwitcher
